import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center py-20">
            <ShoppingBag className="w-24 h-24 mx-auto mb-6 text-purple-400" />
            <h2 className="text-4xl font-bold text-white mb-4">Your Cart is Empty</h2>
            <p className="text-purple-200 mb-8">Start adding some magical ritual oils!</p>
            <Link to="/products">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold px-8">
                Shop Products
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = getCartTotal();
  const shipping = subtotal >= 100 ? 0 : 8;
  const freeShippingProgress = Math.min((subtotal / 100) * 100, 100);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4">
        <h1 className="text-5xl font-bold text-white mb-8">Shopping Cart</h1>

        {/* Free Shipping Progress */}
        {subtotal < 100 && (
          <Card className="bg-purple-800/50 border-purple-600 mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white">
                  Add <span className="font-bold text-yellow-400">${(100 - subtotal).toFixed(2)}</span> more for FREE shipping!
                </p>
                <p className="text-purple-300 text-sm">{freeShippingProgress.toFixed(0)}%</p>
              </div>
              <div className="w-full bg-purple-700 rounded-full h-2">
                <div 
                  className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${freeShippingProgress}%` }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <Card key={`cart-${item.id}-${item.size}`} className="bg-purple-800/50 border-purple-600">
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <div className="w-24 h-24 flex-shrink-0">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover rounded"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-1">{item.name}</h3>
                      <p className="text-purple-300 text-sm mb-2">Size: {item.size}</p>
                      <p className="text-yellow-400 font-semibold">${item.price.toFixed(2)}</p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex flex-col items-end gap-4">
                      <button
                        onClick={() => removeFromCart(item.product_id, item.size)}
                        className="text-purple-300 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1, item.max_quantity)}
                          className="w-8 h-8 bg-purple-700 hover:bg-purple-600 rounded flex items-center justify-center text-white"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-12 text-center text-white font-semibold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1, item.max_quantity)}
                          disabled={item.max_quantity && item.quantity >= item.max_quantity}
                          className={`w-8 h-8 rounded flex items-center justify-center text-white ${
                            item.max_quantity && item.quantity >= item.max_quantity
                              ? 'bg-purple-900 cursor-not-allowed opacity-50'
                              : 'bg-purple-700 hover:bg-purple-600'
                          }`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex flex-col items-end">
                        <p className="text-white font-semibold">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        {item.max_quantity && item.quantity >= item.max_quantity && (
                          <p className="text-purple-300 text-xs mt-1">
                            Max qty reached
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="bg-purple-800/50 border-purple-600 sticky top-24">
              <CardContent className="pt-6">
                <h2 className="text-2xl font-bold text-white mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-purple-200">
                    <span>Subtotal</span>
                    <span className="font-semibold">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-purple-200">
                    <span>Shipping</span>
                    <span className="font-semibold">
                      {shipping === 0 ? (
                        <span className="text-green-400">FREE</span>
                      ) : (
                        `$${shipping.toFixed(2)}`
                      )}
                    </span>
                  </div>
                  <div className="border-t border-purple-600 pt-4">
                    <div className="flex justify-between text-white text-lg font-bold">
                      <span>Estimated Total</span>
                      <span>${(subtotal + shipping).toFixed(2)}</span>
                    </div>
                    <p className="text-purple-300 text-sm mt-2">Tax calculated at checkout</p>
                  </div>
                </div>

                <Button 
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold py-6 text-lg"
                >
                  Proceed to Checkout
                  <ArrowRight className="ml-2" />
                </Button>

                <Link to="/products">
                  <Button 
                    variant="outline"
                    className="w-full mt-4 border-purple-600 text-purple-200 hover:bg-purple-800"
                  >
                    Continue Shopping
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
